import { useState, useEffect } from "react";
import { Pressable, ActivityIndicator, Switch, Linking, Platform, View, Text } from "react-native";
import styled from "styled-components/native";
import KeyboardAwareScroll from "@/components/KeyboardAwareScroll";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useClub } from "@/contexts/ClubContext";
import { useAuth } from "@/contexts/AuthContext";
import { useRouter } from "expo-router";
import { colors, radius, font } from "@/lib/theme";
import { startConnectOnboarding, checkConnectStatus, getStripeDashboardLink } from "@/lib/stripe-connect";
import { useConfirm } from "@/hooks/useConfirm";
import ConfirmSheet from "@/components/ConfirmSheet";
import ContentWidth from "@/components/ContentWidth";

const Section = styled.View`
  padding: 20px;
`;

const SectionLabel = styled.Text`
  font-size: 12px;
  font-weight: ${font.extrabold};
  color: ${colors.inkMuted};
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 12px;
`;

const Card = styled.View`
  background-color: ${colors.white};
  border-radius: ${radius.lg}px;
  border: 1px solid ${colors.line};
  padding: 16px;
`;

const Label = styled.Text`
  font-size: 11px;
  font-weight: ${font.bold};
  color: ${colors.inkMuted};
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 8px;
  margin-top: 16px;
`;

const Input = styled.TextInput`
  background-color: ${colors.bg};
  border: 1px solid ${colors.line};
  border-radius: ${radius.md}px;
  padding: 12px 14px;
  font-size: 15px;
  color: ${colors.ink};
`;

const SaveBtn = styled.View`
  background-color: ${colors.brand};
  border-radius: ${radius.card}px;
  padding: 16px;
  align-items: center;
  margin: 20px;
`;

const SaveBtnText = styled.Text`
  color: #fff;
  font-size: 16px;
  font-weight: ${font.bold};
`;

const HintText = styled.Text`
  font-size: 13px;
  color: ${colors.inkMuted};
  line-height: 20px;
  margin-top: 4px;
`;

const LoadingContainer = styled.View`
  flex: 1;
  background-color: ${colors.bg};
  justify-content: center;
  align-items: center;
`;

const Spacer = styled.View`
  height: 32px;
`;

const ToggleRow = styled.View`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding-vertical: 12px;
`;

const ToggleLabel = styled.Text`
  font-size: 15px;
  font-weight: ${font.semibold};
  color: ${colors.ink};
`;

const ConnectHint = styled.Text`
  font-size: 13px;
  color: ${colors.inkMuted};
  line-height: 19px;
  margin-top: 16px;
  margin-bottom: 12px;
`;

const ConnectBtn = styled.View`
  background-color: ${colors.brand};
  border-radius: ${radius.card}px;
  padding: 14px;
  align-items: center;
`;

const ConnectBtnText = styled.Text`
  color: #fff;
  font-size: 15px;
  font-weight: ${font.bold};
`;

const ConnectStatus = styled.View`
  flex-direction: row;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
  margin-bottom: 12px;
`;

const StatusDot = styled.View`
  width: 8px;
  height: 8px;
  border-radius: 4px;
  background-color: ${colors.brand};
`;

const StatusText = styled.Text`
  font-size: 14px;
  font-weight: ${font.semibold};
  color: ${colors.brandDark};
`;

const DashboardBtn = styled.View`
  background-color: ${colors.bg};
  border: 1px solid ${colors.line};
  border-radius: ${radius.card}px;
  padding: 14px;
  align-items: center;
`;

const DashboardBtnText = styled.Text`
  font-size: 15px;
  font-weight: ${font.bold};
  color: ${colors.ink};
`;

const RefreshText = styled.Text`
  font-size: 13px;
  font-weight: ${font.semibold};
  color: ${colors.brand};
  text-align: center;
  padding-vertical: 12px;
  margin-top: 8px;
`;

export default function ClubSettingsScreen() {
  const router = useRouter();
  const { club } = useClub();
  const { user } = useAuth();
  const { confirmProps, confirm } = useConfirm();
  const [name, setName] = useState(club?.name || "");
  const [shortName, setShortName] = useState(club?.shortName || "");
  const [address, setAddress] = useState((club as any)?.address || "");
  const [phone, setPhone] = useState((club as any)?.phone || "");
  const [email, setEmail] = useState((club as any)?.email || "");
  const [playCricketUrl, setPlayCricketUrl] = useState((club as any)?.playCricketUrl || "");
  const [clubbuzzUrl, setClubbuzzUrl] = useState((club as any)?.clubbuzzUrl || "");
  const [logoUrl, setLogoUrl] = useState(club?.logoUrl || "");
  const [saving, setSaving] = useState(false);
  const [connectLoading, setConnectLoading] = useState(false);
  const [availabilityLeadDays, setAvailabilityLeadDaysState] = useState(club?.availabilityLeadDays ?? 7);
  const [paymentInfoExpanded, setPaymentInfoExpanded] = useState(false);

  if (!club) {
    return <LoadingContainer><ActivityIndicator size="large" color={colors.brand} /></LoadingContainer>;
  }

  const isConnected = !!club.stripeOnboardingComplete;

  // Auto-check Stripe status if account exists but onboarding not complete
  useEffect(() => {
    if (club.stripeAccountId && !club.stripeOnboardingComplete) {
      checkConnectStatus(club.id).catch(() => {});
    }
  }, [club.id, club.stripeAccountId, club.stripeOnboardingComplete]);

  const handleConnectBank = async (forceNew = false) => {
    setConnectLoading(true);
    try {
      const returnUrl = Platform.OS === "web"
        ? window.location.href
        : "https://app.inthecrease.uk";
      const { url } = await startConnectOnboarding(club.id, returnUrl, forceNew);
      if (Platform.OS === "web") {
        window.location.href = url;
        return;
      } else {
        await Linking.openURL(url);
        // Refresh Stripe status when they return to the app
        try { await checkConnectStatus(club.id); } catch {}
      }
    } catch (err: any) {
      console.error("Connect error:", err);
    } finally {
      setConnectLoading(false);
    }
  };

  const handleViewDashboard = async () => {
    setConnectLoading(true);
    try {
      const { url } = await getStripeDashboardLink(club.id);
      if (Platform.OS === "web") {
        window.open(url, "_blank");
      } else {
        await Linking.openURL(url);
      }
    } catch (err: any) {
      console.error("Dashboard link error:", err);
    } finally {
      setConnectLoading(false);
    }
  };

  const handleRefreshStatus = async () => {
    setConnectLoading(true);
    try {
      await checkConnectStatus(club.id);
    } catch (err: any) {
      console.error("Check status error:", err);
    } finally {
      setConnectLoading(false);
    }
  };

  const setLeadDays = async (val: number) => {
    setAvailabilityLeadDaysState(val);
    await updateDoc(doc(db, "clubs", club.id), { availabilityLeadDays: val });
  };

  const handleSave = async () => {
    setSaving(true);
    await updateDoc(doc(db, "clubs", club.id), {
      name: name.trim(),
      shortName: shortName.trim(),
      address: address.trim(),
      phone: phone.trim(),
      email: email.trim(),
      playCricketUrl: playCricketUrl.trim(),
      clubbuzzUrl: clubbuzzUrl.trim(),
      logoUrl: logoUrl.trim() || null,
    });

    setSaving(false);
    router.back();
  };

  return (
    <KeyboardAwareScroll style={{ flex: 1, backgroundColor: colors.bg }} showsVerticalScrollIndicator={false}>
      <ContentWidth>
        <Section>
          <SectionLabel>Club Details</SectionLabel>
          <Card>
            <Label style={{ marginTop: 0 }}>Club Name</Label>
            <Input value={name} onChangeText={setName} placeholder="e.g. Peterborough Town CC" placeholderTextColor={colors.inkMuted} />

            <Label>Short Name</Label>
            <Input value={shortName} onChangeText={setShortName} placeholder="e.g. PTCC" placeholderTextColor={colors.inkMuted} />

            <Label>Address</Label>
            <Input value={address} onChangeText={setAddress} placeholder="Ground address" placeholderTextColor={colors.inkMuted} multiline />

            <Label>Phone</Label>
            <Input value={phone} onChangeText={setPhone} placeholder="Contact number" placeholderTextColor={colors.inkMuted} keyboardType="phone-pad" />

            <Label>Email</Label>
            <Input value={email} onChangeText={setEmail} placeholder="Club email" placeholderTextColor={colors.inkMuted} keyboardType="email-address" autoCapitalize="none" />

            <Label>Club Logo URL</Label>
            <Input value={logoUrl} onChangeText={setLogoUrl} placeholder="https://..." placeholderTextColor={colors.inkMuted} autoCapitalize="none" />
            <HintText>Direct link to your club's badge image</HintText>
          </Card>
        </Section>

        <Section>
          <SectionLabel>Payments</SectionLabel>
          {!isConnected ? (
            /* Payments enabled but not connected */
            <Card>
              <ConnectHint style={{ marginTop: 0 }}>
                Connect your bank account to collect match fees and membership subs from members.
              </ConnectHint>
              <Pressable onPress={() => handleConnectBank()} disabled={connectLoading} style={({ pressed }) => ({ opacity: pressed ? 0.9 : 1 })}>
                <ConnectBtn>
                  {connectLoading ? <ActivityIndicator color="#fff" size="small" /> : <ConnectBtnText>Connect bank account</ConnectBtnText>}
                </ConnectBtn>
              </Pressable>
              <Pressable
                onPress={() => router.push("/(app)/help?contactOnly=true")}
                style={({ pressed }) => ({ marginTop: 12, opacity: pressed ? 0.7 : 1 })}
              >
                <Text style={{ fontSize: 13, color: colors.inkMuted, textAlign: "center" }}>
                  Having trouble? <Text style={{ color: colors.brand, fontWeight: "600" }}>Contact us</Text>
                </Text>
              </Pressable>
            </Card>
          ) : (
            /* Connected */
            <View style={{
              backgroundColor: colors.brand50,
              borderRadius: radius.lg,
              borderWidth: 1,
              borderColor: colors.brandLight,
              padding: 16,
            }}>
              <View style={{ flexDirection: "row", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                <View style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
                  <View style={{
                    width: 32, height: 32, borderRadius: 16,
                    backgroundColor: colors.brand,
                    alignItems: "center", justifyContent: "center",
                  }}>
                    <Text style={{ fontSize: 16, color: "#fff", fontWeight: "700" }}>✓</Text>
                  </View>
                  <View>
                    <Text style={{ fontSize: 15, fontWeight: "800", color: colors.brandDark }}>You're all set</Text>
                    <Text style={{ fontSize: 12, color: colors.brandDark, opacity: 0.7 }}>Bank account connected</Text>
                  </View>
                </View>
                <Pressable
                  onPress={async () => {
                    const ok = await confirm({
                      title: "Switch payment account?",
                      message: "This will create a new bank account connection. Your current account will be replaced once the new one is set up.",
                      confirmLabel: "Switch account",
                      destructive: true,
                    });
                    if (!ok || !club) return;
                    handleConnectBank(true);
                  }}
                  hitSlop={10}
                  style={({ pressed }) => ({ opacity: pressed ? 0.5 : 0.6, padding: 4 })}
                >
                  <Text style={{ fontSize: 14, color: colors.inkMuted }}>✏️</Text>
                </Pressable>
              </View>
            </View>
          )}
        </Section>

        <Section>
          <SectionLabel>Match availability</SectionLabel>
          <Card>
            <Label style={{ marginTop: 0 }}>Open availability</Label>
            <HintText>How many days before each match should players be asked to respond?</HintText>
            <View style={{ flexDirection: "row", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
              {[3, 7, 14, 28].map((d) => {
                const active = availabilityLeadDays === d;
                return (
                  <Pressable
                    key={d}
                    onPress={() => setLeadDays(d)}
                    style={({ pressed }) => ({
                      paddingHorizontal: 16,
                      paddingVertical: 10,
                      borderRadius: radius.md,
                      borderWidth: 1.5,
                      borderColor: active ? colors.brand : colors.line,
                      backgroundColor: active ? colors.brand50 : colors.white,
                      opacity: pressed ? 0.7 : 1,
                    })}
                  >
                    <Text style={{ fontSize: 14, fontWeight: "700", color: active ? colors.brandDark : colors.inkMuted }}>
                      {d} days
                    </Text>
                  </Pressable>
                );
              })}
            </View>
          </Card>
        </Section>

        <Section>
          <SectionLabel>External Links</SectionLabel>
          <Card>
            <Label style={{ marginTop: 0 }}>Play-Cricket URL</Label>
            <Input value={playCricketUrl} onChangeText={setPlayCricketUrl} placeholder="https://yourclub.play-cricket.com" placeholderTextColor={colors.inkMuted} autoCapitalize="none" />
            <HintText>Your club's Play-Cricket page</HintText>

            <Pressable
              onPress={() => router.push("/(app)/import-fixtures")}
              style={({ pressed }) => ({ marginTop: 12, opacity: pressed ? 0.8 : 1 })}
            >
              <View style={{ backgroundColor: colors.brand, borderRadius: radius.md, padding: 14, alignItems: "center" }}>
                <Text style={{ color: "#fff", fontSize: 14, fontWeight: "700" }}>Import fixtures from Play-Cricket</Text>
              </View>
            </Pressable>

            <Label>ClubBuzz URL</Label>
            <Input value={clubbuzzUrl} onChangeText={setClubbuzzUrl} placeholder="https://yourclub.clubbuzz.co.uk" placeholderTextColor={colors.inkMuted} autoCapitalize="none" />
            <HintText>Your club's ClubBuzz website</HintText>
          </Card>
        </Section>

        <Pressable style={({ pressed }) => ({ opacity: pressed ? 0.9 : 1 })} onPress={handleSave} disabled={saving}>
          <SaveBtn>
            {saving ? <ActivityIndicator color="#fff" /> : <SaveBtnText>Save</SaveBtnText>}
          </SaveBtn>
        </Pressable>

        <Spacer />
      </ContentWidth>
      <ConfirmSheet {...confirmProps} />
    </KeyboardAwareScroll>
  );
}
